/**
 * AuthPage — Login / Register page for Adversyn Trading.
 *
 * Two-tab form: Sign In and Create Account.
 * Dark theme using CSS variables matching the rest of the app.
 */

import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Lock, Mail, User, ArrowRight, Loader2 } from 'lucide-react';
import { useAuthStore } from '../hooks/useAuthStore';

interface AuthPageProps {
  onAuthenticated: () => void;
}

export default function AuthPage({ onAuthenticated }: AuthPageProps) {
  const { loading, error, login, register } = useAuthStore();
  const location = useLocation();
  const initialMode: 'login' | 'register' = location.pathname.includes('register') ? 'register' : 'login';
  const [mode, setMode] = useState<'login' | 'register'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const displayError = localError || error;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError(null);

    if (!email.trim() || !password.trim()) {
      setLocalError('Email and password are required.');
      return;
    }

    try {
      if (mode === 'login') {
        await login(email.trim(), password);
      } else {
        await register(email.trim(), password);
      }
      onAuthenticated();
    } catch {
      // Error is already set in the store
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--app-bg, #0a0a0f)',
        padding: '1rem',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: 'var(--card-bg, #111118)',
          border: '1px solid var(--card-border, #1e1e2e)',
          borderRadius: 16,
          padding: '2.5rem 2rem 2rem',
        }}
      >
        {/* Logo / Branding */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div
            style={{
              width: 48,
              height: 48,
              background: '#059669',
              borderRadius: 14,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 12,
            }}
          >
            <span style={{ color: '#fff', fontSize: 18, fontWeight: 900, fontStyle: 'italic' }}>A</span>
          </div>
          <h1
            style={{
              color: 'var(--app-text, #e2e2e8)',
              fontSize: 20,
              fontWeight: 900,
              fontStyle: 'italic',
              textTransform: 'uppercase',
              letterSpacing: '-0.02em',
              margin: 0,
            }}
          >
            Adversyn Trading
          </h1>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            gap: 0,
            marginBottom: '1.5rem',
            background: 'var(--input-bg, #0d0d14)',
            borderRadius: 10,
            padding: 3,
          }}
        >
          {(['login', 'register'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => {
                setMode(tab);
                setLocalError(null);
              }}
              style={{
                flex: 1,
                padding: '0.5rem 0',
                fontSize: 11,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'all 0.15s',
                background: mode === tab ? 'var(--card-bg, #111118)' : 'transparent',
                color: mode === tab ? 'var(--app-text, #e2e2e8)' : 'var(--muted-text, #6b6b80)',
                boxShadow: mode === tab ? '0 1px 4px rgba(0,0,0,0.3)' : 'none',
              }}
            >
              {tab === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Email */}
          <label
            style={{
              display: 'block',
              marginBottom: '0.25rem',
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              color: 'var(--muted-text, #6b6b80)',
              letterSpacing: '0.05em',
            }}
          >
            {mode === 'register' ? <User size={10} style={{ marginRight: 4, display: 'inline' }} /> : <Mail size={10} style={{ marginRight: 4, display: 'inline' }} />}
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            style={{
              width: '100%',
              padding: '0.65rem 0.75rem',
              marginBottom: '1rem',
              background: 'var(--input-bg, #0d0d14)',
              border: '1px solid var(--card-border, #1e1e2e)',
              borderRadius: 8,
              color: 'var(--app-text, #e2e2e8)',
              fontSize: 13,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />

          {/* Password */}
          <label
            style={{
              display: 'block',
              marginBottom: '0.25rem',
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              color: 'var(--muted-text, #6b6b80)',
              letterSpacing: '0.05em',
            }}
          >
            <Lock size={10} style={{ marginRight: 4, display: 'inline' }} />
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
            style={{
              width: '100%',
              padding: '0.65rem 0.75rem',
              marginBottom: '1.25rem',
              background: 'var(--input-bg, #0d0d14)',
              border: '1px solid var(--card-border, #1e1e2e)',
              borderRadius: 8,
              color: 'var(--app-text, #e2e2e8)',
              fontSize: 13,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />

          {/* Error */}
          {displayError && (
            <div
              style={{
                marginBottom: '1rem',
                padding: '0.5rem 0.75rem',
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: 8,
                color: '#ef4444',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {displayError}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '0.7rem 0',
              background: loading ? '#047857' : '#059669',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontSize: 12,
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'background 0.15s',
              opacity: loading ? 0.8 : 1,
            }}
          >
            {loading ? (
              <>
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                {mode === 'login' ? 'Signing In...' : 'Creating Account...'}
              </>
            ) : (
              <>
                {mode === 'login' ? 'Sign In' : 'Create Account'}
                <ArrowRight size={14} />
              </>
            )}
          </button>
        </form>

        {/* Spin keyframe injection */}
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}
